import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components 
import withLocalization from '../../../hoc/withLocalization';
import LoadingSpinner from '../../../elements/LoadingSpinner';
import GenericList from '../../../components/GenericList/GenericList';
import GenericFilter from '../../../components/GenericList/GenericFilter';

//elements
import Button from '../../../components/CustomButton/CustomButton';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import { UsersFromUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget.js';

//assets
import Delete from '../../../assets/img/deleting.svg';
import { confirmAlert } from 'react-confirm-alert';

const member_assignment_statuses = [
    { code: 'assigned', name: 'Assigned' },
    { code: 'unassigned', name: 'Unassigned' },
];

class ProjectMembersForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: { name: '', status: '' },
            appliedFilters: { name: '', status: '' },
            selectedIds: [],
            currentPageUnassignedIds: [],
            currentPageRows: [],
            assignedCount: 0,
            unassignedCount: 0,
            loading: false,
            reloadKey: Date.now(),
            userGroupsValue: null,
            page: 0,
            isPageAddedOrEdited: false,
        };
    }

    setFilter = (field, value) => {
        this.setState(prev => ({ filters: { ...prev.filters, [field]: value } }));
    };

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    };

    onFilter = () => {
        this.setState(prev => ({
            appliedFilters: { ...prev.filters },
            reloadKey: Date.now(),
            selectedIds: [],
            currentPageUnassignedIds: [],
            currentPageRows: [],
            page: 0,
            isPageAddedOrEdited: false
        }));
    };

    updateStatusFilter = (status) => {
        const { appliedFilters } = this.state;
        const newStatus = appliedFilters.status === status ? '' : status;

        this.setState(prev => ({
            filters: { ...prev.filters, status: newStatus }
        }), this.onFilter);
    };

    componentDidMount() {
        this.loadCounts();
    }

    loadCounts = async () => {
        const { projectStore, userStore, commonStore, t, id: projectId } = this.props;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        if (!projectId) return;
        try {
            const res = await projectStore.getProjectMembersCount(projectId, { pmMembers })
            this.setState({
                assignedCount: res.assigned || 0,
                unassignedCount: res.unassigned || 0
            });
            if (this.props.updateMemberCount) {
                this.props.updateMemberCount(res.assigned || 0);
            }
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t(error.message || 'Something went wrong'), null, 'error');
        }
    };

    toggleSelect = (id) => {
        this.setState(prev => {
            const selected = prev.selectedIds.includes(id) ? prev.selectedIds.filter(x => x !== id) : [...prev.selectedIds, id];
            return { selectedIds: selected };
        });
    };

    toggleSelectAll = () => {
        this.setState(prev => {
            const { currentPageUnassignedIds, selectedIds } = prev;
            const allSelected = currentPageUnassignedIds.length > 0 && currentPageUnassignedIds.every(id => selectedIds.includes(id));
            if (allSelected) {
                return { selectedIds: selectedIds.filter(id => !currentPageUnassignedIds.includes(id)) };
            } else {
                return { selectedIds: Array.from(new Set([...selectedIds, ...currentPageUnassignedIds])) };
            }
        });
    };

    fetchRows = (params = {}) => {
        const { appliedFilters, userGroupsValue } = this.state;
        const { projectStore, userStore } = this.props;
        const projectId = this.props.id;
        const { currentUser } = userStore;
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        const options = {
            name: appliedFilters.name || '',
            assigned: appliedFilters.status || 'all',
            page: params.page || 0,
            pagesize: params.pagesize,
            isNotPagination: params.isNotPagination || false,
            pmMembers: pmMembers,
            groupCodes: userGroupsValue ? (Array.isArray(userGroupsValue) ? userGroupsValue.join(',') : userGroupsValue) : ''
        };

        return projectStore.getProjectMembers(projectId, options).then(response => {
            const list = response.rows || [];
            const unassignedIds = list.filter(r => !r.is_assigned).map(r => r.user_id);
            this.setState({
                currentPageUnassignedIds: unassignedIds,
                currentPageRows: list
            });
            return response;
        });
    };

    assignSelected = async () => {
        const { selectedIds } = this.state;
        const { projectStore, commonStore, t } = this.props;
        const projectId = this.props.id;
        if (!selectedIds.length || !projectId) return;
        this.setState({ loading: true });
        try {
            const members = selectedIds.map(id => ({
                user_id: parseInt(id),
                additional_pay: 0,
                vacation_days: 25,
                hours: 40,
                timeframe: 'weekly'
            }));
            await projectStore.saveProjectMembers({ projectId, members });
            commonStore.addNotification(t('Saved'), null, 'success');
            this.setState({ reloadKey: Date.now(), selectedIds: [], isPageAddedOrEdited: true }, () => this.loadCounts());
        } catch (err) {
            commonStore.addNotification(err.message || t('Error assigning members'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    unassignMember = async (userId) => {
        const { projectStore, commonStore, t } = this.props;
        const projectId = this.props.id;
        const row = this.state.currentPageRows.find(r => r.user_id === userId);
        if (row && row.is_assignee) {
            return commonStore.addNotification(t('Cannot remove Manager'), null, 'warning');
        }
        try {
            confirmAlert({
                title: `${t('Confirm')} ?`,
                message: t('Are you sure you want to remove this member from the project?'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: async () => {
                            await projectStore.deleteProjectMember(projectId, userId);
                            commonStore.addNotification(t('Saved'), null, 'success');
                            this.setState({ reloadKey: Date.now(), isPageAddedOrEdited: true }, () => this.loadCounts());
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => {}
                    },
                ]
            })
        } catch (err) {
            commonStore.addNotification(err.message || t('Error removing member'), null, 'error');
        }
    };

    onGroupsChange = (selectedGroups) => {
        const codes = (selectedGroups || []).map(g => g.value).join(',');
        this.setState({ userGroupsValue: codes, reloadKey: Date.now(), selectedIds: [] }, () => this.loadCounts());
    };

    renderCheckboxCell = (row) => {
        const userId = row.original.user_id;
        const isAssigned = !!row.original.is_assigned;
        const isAssignee = !!row.original.is_assignee;
        const { selectedIds } = this.state;
        const isSelected = selectedIds.includes(userId);
        const { t, userStore } = this.props;
        const { currentUser } = userStore;
        const isPm = currentUser.user_type === 'pm';
        const isDisabled = isPm && (isAssignee || userId === currentUser.id);

        return (
            <div className="d-flex align-items-center justify-content-center">
                {isAssigned ? (
                    <OverlayTrigger
                        key="unassign_member"
                        placement="top"
                        overlay={<Tooltip id="tooltip-top">
                            {isDisabled ? t("You don't have enough rights to perform this action") : t('Unassign')}
                        </Tooltip>}
                    >
                        <Button icon_sm fill disabled={isDisabled} onClick={() => this.unassignMember(userId)}>
                            <img src={Delete} alt="delete" />
                        </Button>
                    </OverlayTrigger>
                ) : (
                    <Checkbox
                        inlineBlock
                        isChecked={isSelected}
                        onChange={() => this.toggleSelect(userId)}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                )}
            </div>
        );
    }

    render() {
        const { id, t } = this.props;
        const { filters, appliedFilters, loading, selectedIds, currentPageUnassignedIds, assignedCount, unassignedCount } = this.state;

        if (!id) return <div />;
        if (loading) return <LoadingSpinner />;

        const isAllSelected = currentPageUnassignedIds.length > 0 &&
            currentPageUnassignedIds.every(id => selectedIds.includes(id));

        const columns = [
            {
                Header: t('Member'),
                id: 'fullname',
                textAlign: 'start',
                accessor: 'fullname',
                isSortable: true,
                Cell: ({ row }) => <div className="text-start">{row.original.fullname}</div>
            },
            {
                Header: t('Hours'),
                id: 'hours',
                accessor: 'hours',
                maxWidth: 120,
                Cell: ({ row }) => <div>{row.original.hours || 40}</div>
            },
            {
                Header: t('Timeframe'),
                id: 'timeframe',
                accessor: 'timeframe',
                maxWidth: 120,
                Cell: ({ row }) => <div>{row.original.timeframe || 'weekly'}</div>
            },
            {
                Header: appliedFilters.status === 'assigned' ? t('Unassign') : appliedFilters.status === 'unassigned' ? t('Assign') : `${t('Assign')} / ${t('Unassign')}`,
                id: 'assigned',
                textAlign: 'center',
                accessor: 'user_id',
                Cell: (cellProps) => this.renderCheckboxCell(cellProps.row),
            }
        ];

        return (
            <div>
                <div className='mb-3'>
                <GenericList
                    key={this.state.reloadKey}
                    columns={columns}
                    stickyHeader
                    header={
                        <>
                            <Row className="align-items-center gx-2">
                                <Col xs={12} lg={8} xl={9}>
                                    <Row className="member_status__summary">
                                        <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-start">
                                            <div
                                                className="highlightedgreen" role="button"
                                                onClick={() => this.updateStatusFilter('assigned')}
                                                style={{
                                                    cursor: 'pointer',
                                                    opacity: appliedFilters.status === 'assigned' ? 0.4 : 1
                                                }}
                                            >
                                                {`${t('Assigned')} ${t('Members')}`} : {assignedCount}
                                            </div>
                                        </Col>
                                        <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center">
                                            <div
                                                className="highlightedred" role="button"
                                                onClick={() => this.updateStatusFilter('unassigned')}
                                                style={{
                                                    cursor: 'pointer',
                                                    opacity: appliedFilters.status === 'unassigned' ? 0.4 : 1
                                                }}
                                            >
                                                {`${t('Unassigned')} ${t('Members')}`} : {unassignedCount}
                                            </div>
                                        </Col>
                                        <Col xs="auto" className="mt-2 mt-sm-0 d-flex justify-content-center justify-content-sm-end">
                                            <Checkbox
                                                inlineBlock
                                                isChecked={isAllSelected}
                                                onChange={this.toggleSelectAll}
                                                disabled={currentPageUnassignedIds.length === 0}
                                                label={t("Select All")}
                                            />
                                        </Col>
                                    </Row>
                                </Col>

                                <Col xs="auto" className="d-flex justify-content-center justify-content-lg-end mt-2 mt-lg-0">
                                    {selectedIds.length > 0 && (
                                        <Button onClick={this.assignSelected} title={t('Assign')} fill>
                                            {t('Assign')} ({selectedIds.length})
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} sm={12} md={9} className={'mt-2'}>
                                    <GenericFilter
                                        filters={filters}
                                        setFilter={(name, value) => this.setFilter(name, value)}
                                        onFilter={() => this.onFilter()}
                                        loading={loading}
                                        hasStatusFilter={true}
                                        statusOptions={member_assignment_statuses}
                                        searchFilterPlaceholder={t('Search')}
                                    />
                                </Col>
                                <Col xs={{ span: 3, order: 3 }} sm={{ span: 2, order: 3 }} md={{ span: 3, order: 1 }} className={'mt-3'}>
                                    <UsersFromUserGroupsWidget
                                        value={this.state.userGroupsValue}
                                        onChange={this.onGroupsChange}
                                        updateGroupBasedOnSelectedUsers={() => { }}
                                    />
                                </Col>
                            </Row>
                        </>
                    }
                    requestData={(params) => this.fetchRows(params)}
                    filters={this.state.appliedFilters}
                    page={this.state.page}
                    isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                    handlePageChange={this.handlePageChange}
                    showPageSizeSelector
                    pageSizeListId="project_members"
                />
                </div>
            </div>
        );
    }
}

export default inject('projectStore', 'userStore', 'commonStore')(withLocalization(observer(ProjectMembersForm)));